import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { DynamicFormComponent } from 'app/_dynamic_form/containers/dynamic_form/dynamic-form.component';
import { Globals } from 'app/_globals';
import { ContractorManagerService } from 'app/_services/contractor-manager.service';
import { NotificationService } from 'app/_services/notification.service';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-contractor-managers',
  templateUrl: './contractor-managers.component.html',
})
export class ContractorManagersComponent implements OnInit {
  @ViewChild(DynamicFormComponent) filterForm: DynamicFormComponent;

  users: any[] = [];
  selectedUser: any;

  sidebarVisible = false;
  createUserModal = false;
  createUserForm: FormGroup;
  editUserForm: FormGroup;
  editUserModal = false;

  pageSizes: any = environment.pageSizes;
  pagination: any;
  @ViewChild('paginator', { static: true }) paginator;

  selectedSort: any;

  sortFields: any[] = [
    {
      sortName: 'First Name (asc)',
      sortString: 'user.firstName,asc',
    },
    {
      sortName: 'First Name (desc)',
      sortString: 'user.firstName,desc',
    },
    {
      sortName: 'Last Name (asc)',
      sortString: 'user.lastName,asc',
    },
    {
      sortName: 'Last Name (desc)',
      sortString: 'user.lastName,desc',
    },
  ];

  role: any;
  routeRole: any;

  errorAlerts: any[] = [];

  constructor(
    public loader: LoadingBarService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public globals: Globals,
    public fb: FormBuilder,
    public notificationService: NotificationService,
    public contractorManagerService: ContractorManagerService
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;

    this.createUserForm = fb.group({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      login: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      rPassword: new FormControl('', [Validators.required]),
    });

    this.editUserForm = fb.group({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      login: new FormControl('', [Validators.required]),
    });

    this.selectedSort = this.sortFields[0];
  }

  ngOnInit() {
    this.resetPagination();
    this.getAllMaintenanceManagers();
  }

  public getAllMaintenanceManagers() {
    this.contractorManagerService.getAllContractorManagers(this.pagination, this.selectedSort.sortString).subscribe((data: any) => {
      this.users = data.body;
      this.pagination.totalRecords = data.headers.get('x-total-count');
    });
  }

  /* PAGINATION */
  public resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getAllMaintenanceManagers();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }
  /* PAGINATION END */

  // toggle the create user modal
  public toggleCreateUserModal() {
    this.createUserModal = !this.createUserModal;
  }

  // create user, reset forms, toggle modal, reload data
  public onCreateUserFormSubmit() {
    const values = this.createUserForm.value;
    if (values.password === values.rPassword) {
      const body = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        login: values.login,
        password: values.password,
      };
      this.contractorManagerService.createContractorManager(body).subscribe((data: any) => {
        this.toggleCreateUserModal();
        this.createUserForm.reset();
        this.resetPagination();
        this.getAllMaintenanceManagers();
        Swal.fire('Success', 'Maintenance Manager Created!', 'success');
      });
    } else {
      this.errorAlerts.push({ severity: 'error', summary: 'Passwords do not match!' });
    }
  }

  // Handle onRowSelect of a user
  public async onRowSelect(event) {
    this.selectedUser = event.data;
    const { value: action } = await Swal.fire({
      titleText: event.data.firstName + ' ' + event.data.lastName,
      input: 'select',
      inputOptions: {
        edit: 'Edit',
        changePassword: 'Change Password',
        // 'delete': 'Delete'
      },
      inputPlaceholder: 'Select an option',
      showCancelButton: true,
      inputValidator: (value) => {
        return new Promise((resolve) => {
          resolve(null);
        });
      },
    });

    if (action) {
      if (action === 'edit') {
        this.editUser();
      } else if (action === 'changePassword') {
        this.changeUserPassword();
      } else {
        console.log('No valid option selected');
      }
    }
  }

  // Edit User
  public editUser() {
    this.editUserForm.patchValue({
      firstName: this.selectedUser.firstName,
      lastName: this.selectedUser.lastName,
      email: this.selectedUser.email,
      login: this.selectedUser.login,
    });
    this.toggleEditUserModal();
  }

  public toggleEditUserModal() {
    this.editUserModal = !this.editUserModal;
  }

  public onEditUserFormSubmit() {
    const values = this.editUserForm.value;
    const body = {
      firstName: values.firstName,
      lastName: values.lastName,
      login: values.login,
      email: values.email,
      skylarkUserId: this.selectedUser.id,
    };
    this.contractorManagerService.editContractorManager(body).subscribe((data: any) => {
      this.toggleEditUserModal();
      this.editUserForm.reset();
      this.resetPagination();
      this.selectedUser = null;
      this.getAllMaintenanceManagers();
      Swal.fire('Success', 'Support User Edited!', 'success');
    });
  }

  // Change User Password
  public changeUserPassword() {
    (Swal as any)
      .mixin({
        input: 'text',
        confirmButtonText: 'Next &rarr;',
        showCancelButton: true,
        progressSteps: ['1', '2'],
      })
      .queue([
        {
          title: 'Enter New Password',
          text: 'Please enter a new password.',
        },
      ])
      .then((result) => {
        if (result.value) {
          Swal.fire({
            title: 'Confirm',
            text: 'Change Maintenance Manager password to the following?',
            html: 'New Password: <pre><code>' + JSON.stringify(result.value[0]) + '</code></pre>',
            confirmButtonText: 'Confirm',
          }).then((res) => {
            if (res.value) {
              const body = {
                newPassword: result.value[0],
                skylarkUserId: this.selectedUser.id,
              };
              this.contractorManagerService.updateContractorManagerPassword(body).subscribe((response) => {
                Swal.fire('Success', 'Maintenance Manager Password Changed!', 'success');
              });
            }
          });
        }
      });
  }

  // Handle sort change and reload data
  // TO DO
  public sortChange(event) {
    this.selectedSort = event;
    this.goToFirstPage();
    this.getAllMaintenanceManagers();
  }
}
